import React, { useState, useEffect, useContext, useCallback } from "react";
import Footer from "../components/Footer";
import axios from "axios";
import NavigationBar from "../components/Navigation";
import { placeholderImage } from "../constants";
import {
  useParams,
  useSearchParams,
} from "react-router-dom";
import { UserContext } from '../UserContext';
import { db, auth } from "../config/firebasedeconfig";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Helmet } from "react-helmet-async"; // Import de Helmet pour le SEO
import { useLanguage } from '../contexts/LanguageContext';
import ActivityGainToast from "../components/ActivityGainToast";
import SuperStarVideoGallery from "../components/SuperStarVideoGallery";
import ActorHeroBackdrop from "../components/ActorHeroBackdrop";
import ActorPalmaresPopover from "../components/ActorPalmaresPopover";
import ActorMovieRolePopover from "../components/ActorMovieRolePopover";
import DirectorMovieRolePopover from "../components/DirectorMovieRolePopover";
import {
  getSuperStarProfile
} from "../utils/superStarRegistry";
import {
  calculateDirectorSuperStarScore,
  calculateSuperStarAutoScore,
} from "../utils/superStarUtils";
import {
  getQuebecSuperStarEligibility,
} from "../utils/superStarRegionalUtils";
import {
  findQuebecProduction
} from "../utils/quebecProductionUtils";

// ======================================================
// MOVIEFRENZY — SUPERSTAR
//
// ======================================================
// MOVIEFRENZY — SUPERSTAR 360°
// Moteur hybride automatique + règles éditoriales.
// ======================================================

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;

async function fetchWikipediaBiography(wikidataId, language) {
  if (
    !/^Q[1-9]\d*$/.test(wikidataId || "") ||
    !["fr", "en", "es", "de"].includes(language)
  ) {
    return null;
  }

  try {
    const { data: wikidata } = await axios.get(
      "https://www.wikidata.org/w/api.php",
      {
        timeout: 5000,
        withCredentials: false,
        params: {
          action: "wbgetentities",
          ids: wikidataId,
          props: "sitelinks",
          sitefilter: `${language}wiki`,
          format: "json",
          origin: "*",
        },
      }
    );

    const title =
      wikidata.entities?.[wikidataId]?.sitelinks?.[`${language}wiki`]?.title;

    if (!title) return null;

    const { data: wikipedia } = await axios.get(
      `https://${language}.wikipedia.org/w/api.php`,
      {
        timeout: 5000,
        withCredentials: false,
        params: {
          action: "query",
          titles: title,
          prop: "extracts|info|pageprops",
          exintro: 1,
          explaintext: 1,
          inprop: "url",
          ppprop: "wikibase_item|disambiguation",
          redirects: 1,
          format: "json",
          formatversion: 2,
          origin: "*",
        },
      }
    );

    const page = wikipedia.query?.pages?.[0];
    const text = page?.extract?.trim() || "";

    // Vérifier que l'article correspond bien à la personne TMDB.
    if (
      !text ||
      page?.missing ||
      page?.pageprops?.wikibase_item !== wikidataId ||
      page?.pageprops?.disambiguation !== undefined
    ) {
      return null;
    }

    const url = new URL(page.fullurl);

    if (
      url.protocol !== "https:" ||
      url.hostname !== `${language}.wikipedia.org`
    ) {
      return null;
    }

    return { text, url: url.href };
  } catch {
    // Si Wikipédia ne répond pas, la page poursuit ses autres replis.
    return null;
  }
}

const ActeurDetails = (props) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [detail, setDetail] = useState({});

  const requestedRole =
    searchParams.get("role")?.toLowerCase();

  const tmdbPrimaryRole =
    detail.known_for_department?.toLowerCase() === "directing"
      ? "director"
      : "actor";

  const primaryRole =
    requestedRole === "director" ||
      requestedRole === "actor"
      ? requestedRole
      : tmdbPrimaryRole;

  const heroContextKey = `${id}-${primaryRole}`;

  const placeholderImage = "https://zupimages.net/up/23/14/zdoe.jpg";
  const { t, currentLanguage } = useLanguage();
  const { isLoggedIn } = useContext(UserContext);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [
    showIneligibleFavoriteMessage,
    setShowIneligibleFavoriteMessage,
  ] = useState(false);
  const [activityGainToast, setActivityGainToast] = useState(null);
  const [superStarMovies, setSuperStarMovies] = useState([]);
  const [heroSelection, setHeroSelection] = useState(null);
  const [
    displayedHeroMovie,
    setDisplayedHeroMovie
  ] = useState(null);

  // MOVIEFRENZY — signal visuel :
  // indique à SuperStarVideoGallery qu'un nouveau Hero Backdrop
  // vient réellement d'être chargé et affiché.
  const [heroBackdropCue, setHeroBackdropCue] = useState(0);

  const handleBackdropReady = useCallback(
    (movie) => {
      if (!movie?.id) return;

      setDisplayedHeroMovie({
        actorId: id,
        role: primaryRole,
        movie,
      });

      // Le nouveau Hero Backdrop est réellement chargé.
      // On prévient maintenant SuperStarVideoGallery
      // afin d'afficher brièvement les flèches vers le haut.
      setHeroBackdropCue((previousValue) => previousValue + 1);
    },
    [id, primaryRole]
  );

  const handleBackdropSelect = useCallback(async (movie) => {
    if (!movie?.id) return;

    setHeroSelection({
      actorId: id,
      role: primaryRole,
      movie,
      isQuebecProduction: false,
    });

    const quebecProductionInfo =
      await findQuebecProduction({
        tmdbId: movie.id,
        title: movie.title,
        originalTitle: movie.original_title,
        releaseDate: movie.release_date,
        directors: [],
      });

    setHeroSelection((currentSelection) => {
      // Évite qu'une ancienne requête modifie
      // un autre film ou un autre rôle sélectionné entre-temps.
      if (
        currentSelection?.actorId !== id ||
        currentSelection?.role !== primaryRole ||
        Number(currentSelection?.movie?.id) !== Number(movie.id)
      ) {
        return currentSelection;
      }

      return {
        ...currentSelection,
        isQuebecProduction:
          quebecProductionInfo?.isQuebecProduction === true,
      };
    });
  }, [id, primaryRole]);

  useEffect(() => {
    setHeroSelection(null);
    setDisplayedHeroMovie(null);
    setHeroBackdropCue(0);
  }, [heroContextKey]);

  const [personRoles, setPersonRoles] = useState([]);
  const superStarProfile =
    getSuperStarProfile(id);

  const [isSuperStar, setIsSuperStar] =
    useState(false);

  const [superStarEvaluation, setSuperStarEvaluation] =
    useState(null);

  const showSuperStarDebug =
    new URLSearchParams(window.location.search)
      .get("superstarDebug") === "1";

  useEffect(() => {
    if (!id) return;

    let isCancelled = false;
    setDetail({});

    const fetchActorDetails = async () => {
      try {
        const language =
          currentLanguage === "en"
            ? "en-US"
            : `${currentLanguage}-${currentLanguage.toUpperCase()}`;

        const { data } = await axios.get(
          `https://api.themoviedb.org/3/person/${id}`,
          {
            params: {
              api_key: TMDB_API_KEY,
              language,
              append_to_response: "external_ids",
            },
          }
        );

        if (isCancelled) return;

        let biography = data.biography?.trim() || "";
        let biographyLanguage = currentLanguage;
        let biographySourceUrl = null;

        // Afficher la fiche pendant la recherche d'une biographie.
        setDetail({
          ...data,
          biography,
          biographyLanguage,
          biographySourceUrl,
          biographyLoading: !biography,
        });

        if (!biography) {
          const wikipedia = await fetchWikipediaBiography(
            data.external_ids?.wikidata_id,
            currentLanguage
          );

          if (isCancelled) return;

          if (wikipedia) {
            biography = wikipedia.text;
            biographySourceUrl = wikipedia.url;
          }
        }

        // Dernier repli : TMDB en anglais.
        if (!biography && currentLanguage !== "en") {
          try {
            const { data: englishData } = await axios.get(
              `https://api.themoviedb.org/3/person/${id}`,
              {
                params: {
                  api_key: TMDB_API_KEY,
                  language: "en-US",
                },
                timeout: 5000,
              }
            );

            biography = englishData.biography?.trim() || "";

            if (biography) biographyLanguage = "en";
          } catch {
            // Conserver les autres informations si ce repli échoue.
          }
        }

        if (!isCancelled) {
          setDetail({
            ...data,
            biography,
            biographyLanguage,
            biographySourceUrl,
            biographyLoading: false,
          });
        }
      } catch {
        if (!isCancelled) {
          console.warn("Impossible de charger la fiche de la personne.");
        }
      }
    };

    fetchActorDetails();

    return () => {
      isCancelled = true;
    };
  }, [id, currentLanguage]);

  useEffect(() => {
    setSuperStarEvaluation(null);
    setPersonRoles([]);
    setIsSuperStar(false);
    setSuperStarMovies([]);

    if (!id) {
      setIsSuperStar(false);
      setSuperStarMovies([]);
      setPersonRoles([]);
      return undefined;
    }

    // if (superStarProfile?.mode === "force-off") {
    //   setIsSuperStar(false);
    //   setSuperStarMovies([]);
    //   return undefined;
    // }

    let isCancelled = false;

    const fetchSuperStarMovies = async () => {
      try {
        const response = await axios.get(
          `https://api.themoviedb.org/3/person/${id}/movie_credits?api_key=${TMDB_API_KEY}&language=${currentLanguage === "en"
            ? "en-US"
            : currentLanguage +
            "-" +
            currentLanguage.toUpperCase()
          }`
        );

        if (isCancelled) return;

        const actingCredits =
          Array.isArray(response.data.cast)
            ? response.data.cast
            : [];

        const directingCredits =
          Array.isArray(response.data.crew)
            ? response.data.crew.filter(
              (credit) =>
                credit.job?.toLowerCase() === "director"
            )
            : [];

        // Les apparitions documentaires comme « Self »
        // ne suffisent pas à qualifier quelqu’un d’acteur.
        const meaningfulActingCredits =
          actingCredits.filter((credit) => {
            const character = String(
              credit.character || ""
            )
              .trim()
              .toLowerCase();

            return (
              character &&
              !character.includes("self") &&
              !character.includes("archive footage") &&
              !character.includes("archival footage")
            );
          });

        const availableRoles = new Set();

        const isKnownForActing =
          detail.known_for_department?.toLowerCase() === "acting";

        const substantialActingCredits =
          meaningfulActingCredits.filter(
            (credit) =>
              Number.isFinite(Number(credit.order)) &&
              Number(credit.order) <= 10 &&
              Number(credit.vote_count || 0) >= 100
          );

        if (
          isKnownForActing ||
          substantialActingCredits.length >= 3
        ) {
          availableRoles.add("actor");
        }

        if (directingCredits.length > 0) {
          availableRoles.add("director");
        }

        // Le rôle demandé est utilisé seulement s’il existe réellement
        // dans les crédits de cette personnalité.
        const evaluationRole = availableRoles.has(primaryRole)
          ? primaryRole
          : availableRoles.has(tmdbPrimaryRole)
            ? tmdbPrimaryRole
            : availableRoles.has("actor")
              ? "actor"
              : availableRoles.has("director")
                ? "director"
                : primaryRole;

        const isDirectorEvaluation =
          evaluationRole === "director";

        // Le rôle demandé dans l’URL est présenté en premier.
        const detectedRoles = [
          evaluationRole,
          "actor",
          "director",
        ].filter(
          (role, index, allRoles) =>
            availableRoles.has(role) &&
            allRoles.indexOf(role) === index
        );

        setPersonRoles(detectedRoles);

        // Le veto éditorial empêche SUPERSTAR,
        // mais pas la détection des professions.
        if (superStarProfile?.mode === "force-off") {
          setIsSuperStar(false);
          setSuperStarMovies([]);
          return;
        }

        // Les acteurs sont évalués sur leurs rôles d’interprétation.
        // Les réalisateurs sont évalués uniquement sur les films réalisés.
        const fullCredits = isDirectorEvaluation
          ? directingCredits
          : actingCredits;

        const scoreOptions = {
          credits: fullCredits,
          prestige: superStarProfile?.prestige ?? 0,
          legacy: superStarProfile?.legacy ?? 0,
          editorialOverride: superStarProfile?.editorialOverride ?? null,
          regionalIcon: superStarProfile?.regionalIcon ?? null,
        };

        let evaluation = isDirectorEvaluation
          ? calculateDirectorSuperStarScore(scoreOptions)
          : calculateSuperStarAutoScore(scoreOptions);

        let regionalAuto = null;

        // Complément pour les acteurs non admis par les règles existantes.
        if (
          !isDirectorEvaluation &&
          superStarProfile?.mode !== "force-on" &&
          !evaluation.recommendedSuperStar
        ) {
          regionalAuto = await getQuebecSuperStarEligibility({
            credits: actingCredits,
            wikidataId:
              Number(detail.id) === Number(id)
                ? detail.external_ids?.wikidata_id
                : null,
          });

          if (isCancelled) return;

          if (regionalAuto.qualified) {
            evaluation = calculateSuperStarAutoScore({
              ...scoreOptions,
              regionalIcon: true,
            });
          }
        }

        evaluation = {
          ...evaluation,
          regionalAuto,
          regionalScope: regionalAuto?.qualified
            ? regionalAuto.scope
            : (superStarProfile?.regionalScope ?? null),
        };

        setSuperStarEvaluation(evaluation);

        const nextIsSuperStar =
          superStarProfile?.mode === "force-on"
            ? true
            : evaluation.recommendedSuperStar;

        setIsSuperStar(nextIsSuperStar);

        if (!nextIsSuperStar) {
          setSuperStarMovies([]);
          return;
        }

        const seenMovieIds = new Set();

        const availableMovies = [...fullCredits]
          .filter(
            (movie) =>
              movie?.id &&
              movie?.poster_path
          )

          .filter((movie) => {
            if (seenMovieIds.has(movie.id)) {
              return false;
            }

            seenMovieIds.add(movie.id);
            return true;
          });

        const featuredMovieIds =
          Array.isArray(superStarProfile?.featuredMovieIds)
            ? superStarProfile.featuredMovieIds
            : [];

        let selectedMovies;

        if (featuredMovieIds.length > 0) {
          // Sélection éditoriale MovieFrenzy
          const moviesById = new Map(
            availableMovies.map((movie) => [
              Number(movie.id),
              movie,
            ])
          );

          selectedMovies = featuredMovieIds
            .map((movieId) =>
              moviesById.get(Number(movieId))
            )
            .filter(Boolean)
            .slice(0, 8);
        } else {
          // Fallback automatique actuel
          selectedMovies = [...availableMovies]
            .sort(
              (a, b) =>
                Number(b.vote_count || 0) -
                Number(a.vote_count || 0)
            )
            .slice(0, 8);
        }

        setSuperStarMovies(selectedMovies);

      } catch (error) {
        console.error(
          "[MovieFrenzy] Erreur SuperStar movie credits:",
          error
        );

        if (!isCancelled) {
          setSuperStarMovies([]);
        }
      }
    };

    fetchSuperStarMovies();

    return () => {
      isCancelled = true;
    };
  }, [
    id,
    currentLanguage,
    superStarProfile,
    primaryRole,
    detail.known_for_department,
    detail.id,
    detail.external_ids?.wikidata_id,
    tmdbPrimaryRole
  ]);

  const handleImageError = (event) => {
    event.target.src = placeholderImage;
    console.log("Image error occurred. Placeholder image set.");
    console.log("Profile path:", detail.profile_path);
  };

  const clampFavoriteCount = (items = []) => {
    if (!Array.isArray(items)) {
      return 0;
    }

    return Math.min(items.length, 3);
  };

  const addFavoriteActor = async () => {
    try {
      if (!isLoggedIn || !detail) return;

      // Seules les personnalités possédant un véritable rôle
      // d’interprétation sont admissibles aux acteurs favoris.
      if (!personRoles.includes("actor")) {
        setShowIneligibleFavoriteMessage(true);

        setTimeout(() => {
          setShowIneligibleFavoriteMessage(false);
        }, 3000);

        return;
      }

      const userId = auth.currentUser?.uid;

      if (!userId) {
        console.error("Aucun utilisateur connecté.");
        return;
      }

      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.error("No such user!");
        return;
      }

      const userData = userSnap.data();
      const currentFavorites = userData.favoriteActors || [];

      // Vérifier si l'acteur est déjà dans les favoris
      if (currentFavorites.some(actor => actor.id === detail.id)) {
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
        }, 3000);
        return;
      }

      // Vérifier si l'utilisateur a déjà 3 acteurs favoris
      if (currentFavorites.length >= 3) {
        setShowLimitMessage(true);
        setTimeout(() => {
          setShowLimitMessage(false);
        }, 3000);
        return;
      }

      const newFavoriteActors = [...currentFavorites, detail];
      const nextFavoriteActorsCount = clampFavoriteCount(newFavoriteActors);

      await updateDoc(userRef, {
        favoriteActors: newFavoriteActors,
        "xp.sources.favoriteActors": nextFavoriteActorsCount,
        "xp.updatedAt": serverTimestamp(),
      });

      setActivityGainToast({
        id: `favorite-actor-${detail.id}-${Date.now()}`,
        points: "+1.5",
        unitLabel: t("profilMember.activity"),
        label: "",
        variant: "favorite",
      });

      setShowConfirmation(true);
      setTimeout(() => {
        setShowConfirmation(false);
      }, 3000);
    } catch (error) {
      console.error("Error adding favorite actor:", error);
    }
  };

  const profileImage = detail.profile_path
    ? `https://image.tmdb.org/t/p/original/${detail.profile_path}`
    : placeholderImage;

  return (
    <React.Fragment>
      <Helmet>
        <title>
          {detail.name
            ? `${detail.name} - MovieFrenzy`
            : t("actor.seo.fallbackTitle")}
        </title>

        <link
          rel="canonical"
          href={`https://moviefrenzy.ca/acteurDetails/${id}`}
        />

        <meta
          name="description"
          content={
            detail.name
              ? t("actor.seo.descriptionWithName")
                .replace("{{name}}", detail.name)
              : t("actor.seo.fallbackDescription")
          }
        />
      </Helmet>
      <NavigationBar />
      <ActivityGainToast
        visible={Boolean(activityGainToast)}
        toastId={activityGainToast?.id}
        points={activityGainToast?.points}
        unitLabel={activityGainToast?.unitLabel}
        label={activityGainToast?.label}
        variant={activityGainToast?.variant}
        onDone={() => setActivityGainToast(null)}
      />
      <div
        className="actor-details-container"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          backgroundBlendMode: "overlay",
          backgroundColor: "#200211",
          opacity: "0.9",
          overflowX: "hidden",
        }}
      >
        <div className="actor-details-card">
          <div className="upper-section">
            <ActorHeroBackdrop
              key={heroContextKey}
              movie={
                heroSelection?.actorId === id &&
                  heroSelection?.role === primaryRole
                  ? heroSelection.movie
                  : null
              }
              isQuebecProduction={
                heroSelection?.actorId === id &&
                heroSelection?.role === primaryRole &&
                heroSelection?.isQuebecProduction === true
              }
              onBackdropReady={handleBackdropReady}
            />
            <div className="card-container">
              <div className="card">
                <div
                  className="card-face front"
                  style={{
                    backgroundImage: `url(${profileImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    height: '100%',
                    borderRadius: '10px',
                  }}
                >
                </div>
                <div className="card-face back">
                  <div
                    className="cardFace_SubContainer"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.75)',
                      width: '100%',
                      height: '100%',
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: '1000'
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <video autoPlay muted loop className="videoCard-background">
                      <source src={`${process.env.PUBLIC_URL}/images/Severalturquoiseflashes02.mp4`} type="video/mp4" />
                    </video>
                    {detail.external_ids && detail.external_ids.instagram_id ? (
                      <a
                        href={`https://www.instagram.com/${detail.external_ids.instagram_id}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ zIndex: '1000' }}
                      >
                        <img src={'https://zupimages.net/up/24/41/zwmc.png'} alt="Instagram" className="instagram-icon" />
                      </a>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                        }}
                      >
                        <img src={'https://zupimages.net/up/24/41/zwmc.png'} alt="Instagram" className="instagram-icon" />
                        <p style={{ color: '#FFFFFF', marginTop: "1rem" }}>{t('actor.noInstagram')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isLoggedIn && personRoles.length > 0 && (
              <div className="containButtonFavoriteActor">
                <div className="favoriteActorButton" onClick={addFavoriteActor}>
                  <img src={`${process.env.PUBLIC_URL}/images/actorFavoriteButton02.png`} alt="coeur" className="coeurActor" />
                </div>
                {showConfirmation && (
                  <h2 className="ActorAjoutTitre">{t('actor.addedToFavorites')}</h2>
                )}
                {showLimitMessage && (
                  <h2 className="ActorAjoutTitre">{t('actor.favoritesLimitReached')}</h2>
                )}
                {showIneligibleFavoriteMessage && (
                  <h2 className="ActorAjoutTitre">
                    {t("actor.ineligibleFavorite")}
                  </h2>
                )}
              </div>
            )}
            {/* MOVIEFRENZY — Actions cinéma */}
            {personRoles.length > 0 && (
              <div className="actor-palmares-slot">

                {primaryRole === "actor" &&
                  displayedHeroMovie?.actorId === id &&
                  displayedHeroMovie?.role === "actor" &&
                  displayedHeroMovie?.movie && (
                    <ActorMovieRolePopover
                      actorId={id}
                      actorName={detail.name}
                      movie={displayedHeroMovie.movie}
                    />
                  )}

                {primaryRole === "director" &&
                  displayedHeroMovie?.actorId === id &&
                  displayedHeroMovie?.role === "director" &&
                  displayedHeroMovie?.movie && (
                    <DirectorMovieRolePopover
                      directorId={id}
                      directorName={detail.name}
                      movie={displayedHeroMovie.movie}
                    />
                  )}

                <ActorPalmaresPopover
                  actorId={id}
                  actorName={detail.name}
                />

              </div>
            )}
          </div>
          <div className="actor-info-container">
            <h1 className="actor-details-name">{detail.name}</h1>
            {personRoles.length > 0 && (
              <p className="person-role-list">
                {personRoles
                  .map((role) =>
                    role === "director"
                      ? t("search.actor.departments.directing")
                      : t("search.actor.departments.acting")
                  )
                  .join(" • ")}
              </p>
            )}
            <div className="actor-social-media">
              <h4 className="actor-birthday">{detail.birthday}</h4>
              <p className="actor-place-of-birth">
                {detail.place_of_birth}
              </p>
              <div className="actor-biography">
                <h2>{t("actor.biography")}</h2>

                {detail.id && detail.biographyLoading && (
                  <p>{t("actor.biographyLoading")}</p>
                )}

                {detail.id && !detail.biographyLoading && (
                  <>
                    {detail.biography &&
                      detail.biographyLanguage === "en" &&
                      currentLanguage !== "en" && (
                        <p>
                          <small>
                            {t("actor.biographyEnglishFallback")}
                          </small>
                        </p>
                      )}

                    <p
                      style={{ whiteSpace: "pre-line" }}
                      lang={
                        detail.biography
                          ? detail.biographyLanguage
                          : currentLanguage
                      }
                    >
                      {detail.biography ||
                        t("actor.biographyUnavailable")}
                    </p>

                    {detail.biographySourceUrl && (
                      <p className="actor-biography-source">
                        <small>
                          <a
                            href={detail.biographySourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("actor.biographyWikipediaSource")}
                          </a>

                          {" · "}

                          <a
                            href="https://creativecommons.org/licenses/by-sa/4.0/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            CC BY-SA 4.0
                          </a>
                        </small>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          {showSuperStarDebug && superStarEvaluation && (
            <div
              style={{
                margin: "3rem auto",
                padding: "1.5rem",
                maxWidth: "340px",
                border: "1px solid #00ffff",
                background: "#222",
                color: "#fff",
              }}
            >
              <h3>SUPERSTAR DEBUG — {detail.name}</h3>

              {superStarEvaluation.regionalAuto && (
                <p>
                  Québec auto : {superStarEvaluation.regionalAuto.status}
                  <br />
                  Films retenus : {superStarEvaluation.regionalAuto.filmCount}
                  <br />
                  Rôles top-3 : {superStarEvaluation.regionalAuto.top3Count}
                </p>
              )}

              <p>
                moteur :{" "}
                {superStarEvaluation.role === "director"
                  ? "réalisation"
                  : "interprétation"}
              </p>

              <p>
                careerScore : {superStarEvaluation.careerScore} / 25
              </p>

              <p>
                movieImpactScore : {superStarEvaluation.movieImpactScore} / 30
              </p>

              <p>
                currentHeatScore : {superStarEvaluation.currentHeatScore} / 25
              </p>

              <p>
                automaticTotal : {superStarEvaluation.automaticTotal} / 80
              </p>

              <p>
                prestigeScore : {superStarEvaluation.prestigeScore} / 20
              </p>

              <p>
                FINAL : {superStarEvaluation.finalScore} / 100
              </p>

              <p>
                recommandé :{" "}
                {superStarEvaluation.recommendedSuperStar ? "OUI" : "NON"}
              </p>

              <p>
                raison : {superStarEvaluation.qualificationReason}
              </p>

              <hr />

              <p>
                crédits : {superStarEvaluation.diagnostics?.totalCredits}
              </p>

              <p>
                films 250+ :{" "}
                {superStarEvaluation.diagnostics?.significantMovieCount}
              </p>

              <p>
                films 1000+ :{" "}
                {superStarEvaluation.diagnostics?.strongMovieCount}
              </p>

              <p>
                films 5000+ :{" "}
                {superStarEvaluation.diagnostics?.majorMovieCount}
              </p>

              <hr />

              <p>
                carrière :{" "}
                {superStarEvaluation.diagnostics?.careerSpanYears} ans
              </p>

              {superStarEvaluation.role === "director" ? (
                <>
                  <p>
                    films datés :{" "}
                    {superStarEvaluation.diagnostics?.datedCreditsCount}
                  </p>

                  <p>
                    films 100+ :{" "}
                    {superStarEvaluation.diagnostics?.recognizedMovieCount}
                  </p>

                  <p>
                    films récents :{" "}
                    {superStarEvaluation.diagnostics?.recentCreditsCount}
                  </p>
                </>
              ) : (
                <>
                  <p>
                    rôles top-3 :{" "}
                    {superStarEvaluation.diagnostics?.topBilledCount}
                  </p>

                  <hr />

                  <p>
                    principal 100+ :{" "}
                    {superStarEvaluation.diagnostics?.leadKnownCount}
                  </p>

                  <p>
                    principal 250+ :{" "}
                    {superStarEvaluation.diagnostics?.leadSignificantCount}
                  </p>

                  <p>
                    principal 1000+ :{" "}
                    {superStarEvaluation.diagnostics?.leadStrongCount}
                  </p>

                  <p>
                    principal 5000+ :{" "}
                    {superStarEvaluation.diagnostics?.leadMajorCount}
                  </p>
                </>
              )}

            </div>
          )}
          {isSuperStar && superStarMovies.length > 0 && (
            <SuperStarVideoGallery
              key={heroContextKey}
              onBackdropSelect={handleBackdropSelect}
              heroBackdropCue={heroBackdropCue}
              actorName={detail.name}
              movies={superStarMovies}
              regionalScope={
                superStarProfile?.mode !== "force-on" &&
                  superStarEvaluation?.qualificationReason === "regional-icon"
                  ? (superStarEvaluation?.regionalScope ?? null)
                  : null
              }
            />
          )}
        </div>
      </div>
      <div className="actor-details-footer"
        style={{ maxWidth: '100%', overflowX: 'visible', maxHeight: '100%', overflowY: 'visible', position: 'relative' }}
      >
        <Footer />
      </div>
    </React.Fragment>
  );
};

export default ActeurDetails;





